import {
  AfterViewInit,
  Directive,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TNil } from '@app/app.interface';
import { ICustomCtaValue } from '@campaign-form/campaign-form.interface';
import {
  ILastActiveElementDetails,
  TRemoveConfirmationType,
} from '@campaign-form/form-fields/creators-and-ctas/cta-details.interface';
import { CtaDetailsService } from '@campaign-form/form-fields/creators-and-ctas/cta-details.service';
import { CtaDialogStore } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.store';
import { DeliverableAdvancedSettingsService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-advanced-settings.service';
import { CustomValuesService, TFieldChangeTrackingContext } from '@core/services/custom-values.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { IManualDeliverableStatusConfig } from '@cta-analytics-details/cta-analytics-details-deliverable-card/manual-deliverable-status/manual-devlierable-status.interface';
import { EDeliverableType, EOverwriteStatus, EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateService } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { EPermissions } from '@shared/enums/permissions.enum';
import { snakeCase } from 'lodash';
import { Subscription } from 'rxjs';

export enum ECustomFieldType {
  string = 'string',
  number = 'number',
  date = 'date',
  boolean = 'boolean',
}

export interface ICustomField {
  name: string;
  type: ECustomFieldType;
  isMonitoringDisabled?: boolean;
}

export interface ICustomValueConfig {
  // For the component's own customizable fields
  componentFields?: {
    managedFields: ICustomField[];
    getForm: () => FormGroup;
  };
  // For nested form array with customizable fields
  nestedArray?: {
    managedFields: ICustomField[];
    getFormArray: () => FormArray;
  };
}

@Directive()
export abstract class CustomCtaBaseDirective implements OnInit, AfterViewInit, OnDestroy {
  public readonly form = input.required<FormGroup>();
  public readonly creatorId = input.required<string | TNil>();
  // Wave 10 follow-up - a "group" is a CTA template, so its id doubles as ctaTemplateId; threaded
  // down alongside creatorId so deliverable/execution/kpi change events can report which group they
  // belong to.
  public readonly ctaTemplateId = input<string | undefined>(undefined);
  public readonly isCustomMode = input.required<boolean>();
  public readonly creators = input.required<ICreator[]>();

  public readonly isParentDeleteDisabled = input<boolean>(false);
  public readonly lastActiveElementDetails = input<Map<string, ILastActiveElementDetails>>(new Map());
  public readonly showSeparator = input<boolean>(false);
  public readonly description = input<string | null>(null);
  public readonly isLabelVisible = input<boolean>(true);
  public readonly isViewMode = input<boolean>(false);
  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly showRequiredBadge = input<boolean>(false);
  public readonly isChildOptional = input<boolean>(false);
  public readonly isExpanded = model<boolean>(false);
  public readonly isGlobalTracking = input<boolean>(false);
  public readonly showStatusBadge = input<boolean>(true);
  public readonly manualDeliverableStatusConfig = input<IManualDeliverableStatusConfig>();

  public readonly delete = output<void>();
  public readonly parentCheckboxChange = output<boolean>();

  public readonly isPanelExpanded = computed(
    () =>
      this.isExpanded() ||
      (!this.trackManualExpansionChanges && this.isViewMode()) ||
      CtaDialogStore.expandedErrorDeliverablePanelId() === this.form().get('id')?.value ||
      CtaDialogStore.expandedErrorExecutionPanelId() === this.form().get('id')?.value,
  );
  public readonly analyticsMode = computed(() => {
    return !!this.manualDeliverableStatusConfig();
  });

  public readonly isDisabled = signal<boolean>(false);
  public readonly isChildDeleteDisabled = signal<boolean>(false);
  public readonly lastActiveChildElementDetails = signal<Map<string, ILastActiveElementDetails>>(new Map());
  public readonly showAdvancedSettings = signal<boolean>(false);

  public readonly EPermissions = EPermissions;

  protected abstract readonly customValueConfig: ICustomValueConfig;

  protected readonly dialog = inject(MatDialog);
  protected readonly translate = inject(TranslateService);
  protected readonly deliverableAdvancedSettingsService = inject(DeliverableAdvancedSettingsService);
  // protected (not private) because ExecutionComponent inherits it for trackExecutionTypeChange().
  protected readonly eventTrackingService = inject(EventTrackingService);

  protected removeConfirmationType: TRemoveConfirmationType | null = null;

  protected componentCleanup: (() => void) | null = null;
  protected nestedCleanups: Map<FormGroup, () => void> = new Map();

  protected expandOnAdd = true;

  private readonly customValueCache = new Map<string, boolean>();

  private formSubscriptionsForIsCustom: Subscription[] = [];
  private trackManualExpansionChanges = false;
  private currentCreatorId: string | TNil = null;
  private currentForm: FormGroup | null = null;

  // For #8/#9 (campaign_creation_deliverable_changed / _advanced_settings_changed), passed into
  // startFormValueMonitoring() so it can report the parent deliverable's id/type even when a
  // change comes from a nested-array item (e.g. one keyword's text field), which has no id/type
  // of its own - every deliverable form (TDeliverableForm) carries both fields.
  private readonly getDeliverableId = (): string | undefined => this.form().get('id')?.value as string | undefined;

  protected get trackingContext(): TFieldChangeTrackingContext {
    return {
      deliverableType: this.form().get('type')?.value as EDeliverableType,
      getCtaTemplateId: (): string | undefined => this.ctaTemplateId(),
      getDeliverableId: this.getDeliverableId,
      // Round-3 audit - lets the deliverable events omit campaignCreationCurrentStep for
      // keyword-tracking rows (isGlobalTracking = not a flow-dialog step).
      isGlobalTracking: this.isGlobalTracking(),
      kind: 'deliverable',
    };
  }

  constructor(protected readonly customValuesService: CustomValuesService) {
    this.setupFormChangeEffect();
    this.setupModeChangeEffect();
    this.createPanelExpandedStateEffects();
    this.setupCreatorIdChangeEffect();
  }

  public get name(): FormControl<string | null> {
    return this.form().get('name') as FormControl<string | null>;
  }

  public onToggleAdvancedSettings(): void {
    this.showAdvancedSettings.set(!this.showAdvancedSettings());
  }

  protected autoOpenAdvancedSettingsIfNeeded(
    platformFeature: EPlatformFeature | null | undefined,
    deliverableType: EDeliverableType,
  ): void {
    if (
      this.deliverableAdvancedSettingsService.shouldAutoOpenAdvancedSettings(
        platformFeature,
        deliverableType,
        this.form(),
      )
    ) {
      this.showAdvancedSettings.set(true);
    }
  }

  // Structural nested-array changes (issue #3, manual QA 2026-08-03) - a plain field value change
  // doesn't cover adding/removing a whole row (e.g. a keyword or image), so call these explicitly
  // from the add/remove handlers of components with a nestedArray (text-deliverable,
  // image-deliverable, product-placement).
  // Only ever called from deliverable components with a nestedArray (text/image/product-placement)
  // - the `kind` check is a type-narrowing no-op for them, but guards against misuse if a future
  // execution/kpi component (whose trackingContext.kind isn't 'deliverable') were to call this.
  protected trackNestedArrayItemAdded(fieldName: string, itemIndex: number): void {
    const { trackingContext } = this;

    if (trackingContext.kind !== 'deliverable') {
      return;
    }

    this.customValuesService.trackDeliverableItemAdded(
      { creatorId: this.creatorId(), isCustomMode: this.isCustomMode, trackingContext },
      `${snakeCase(fieldName)}_${itemIndex + 1}`,
    );
  }

  protected trackNestedArrayItemRemoved(
    fieldName: string,
    itemIndex: number,
    removedValue: string | null | undefined,
  ): void {
    const { trackingContext } = this;

    if (trackingContext.kind !== 'deliverable') {
      return;
    }

    this.customValuesService.trackDeliverableItemRemoved(
      { creatorId: this.creatorId(), isCustomMode: this.isCustomMode, trackingContext },
      `${snakeCase(fieldName)}_${itemIndex + 1}`,
      removedValue ?? undefined,
    );
  }

  public ngOnInit(): void {
    this.currentForm = this.form();
    this.initializeValues();

    this.form().markAsUntouched();
    this.form().markAsPristine();
  }

  public ngAfterViewInit(): void {
    if (this.isViewMode()) {
      this.trackManualExpansionChanges = true;
    }
  }

  public ngOnDestroy(): void {
    this.formSubscriptionsForIsCustom?.forEach((subscription) => subscription?.unsubscribe());

    if (this.customValueConfig.componentFields) {
      const form = this.customValueConfig.componentFields.getForm();

      form.enable({ emitEvent: false });
      this.customValuesService.switchToGlobalMode(form, this.customValueConfig.componentFields.managedFields);
    }

    if (this.customValueConfig.nestedArray) {
      const nestedFormArray = this.customValueConfig.nestedArray.getFormArray();
      nestedFormArray.enable({ emitEvent: false });

      nestedFormArray.controls.forEach((control) => {
        const formGroup = control as FormGroup;
        this.customValuesService.switchToGlobalMode(formGroup, this.customValueConfig.nestedArray!.managedFields);
      });
    }

    this.cleanup();
    this.onPanelExpandedChange(false);
  }

  public getCustomValueState(fieldName: string, index?: number): boolean {
    const cacheKey = index !== undefined ? `${fieldName}_${index}` : fieldName;

    if (!this.customValueCache.has(cacheKey)) {
      this.customValueCache.set(cacheKey, this.hasCustomValue(fieldName, index));
    }

    return this.customValueCache.get(cacheKey)!;
  }

  public hasCustomValue(fieldName: string, index?: number): boolean {
    if (typeof index === 'number') {
      const nestedFormArray = this.customValueConfig.nestedArray?.getFormArray();

      if (nestedFormArray) {
        return ((nestedFormArray.at(index).get('customCtaValues')?.value as ICustomCtaValue[]) ?? []).some(
          (value) =>
            value.creatorId === this.creatorId() &&
            (value.valueName === snakeCase(fieldName) || (value.valueName === null && fieldName === 'id')),
        );
      }

      return false;
    }

    return (
      (this.customValueConfig.componentFields?.getForm()?.get('customCtaValues')?.value as ICustomCtaValue[]) ?? []
    ).some(
      (value) =>
        value.creatorId === this.creatorId() &&
        (value.valueName === snakeCase(fieldName) || (value.valueName === null && fieldName === 'id')),
    );
  }

  public onDeleteClick(exclusiveTo: string | TNil): MatDialogRef<unknown> | void {
    if (this.isCustomMode() && !exclusiveTo) {
      return;
    }

    this.delete.emit();
  }

  public onDeleteCheckboxChange(fieldName: string, event: MatCheckboxChange, index?: number): void {
    const noIndex = index === undefined;

    if (event?.checked && fieldName === 'id') {
      if (noIndex) {
        this.enableForm();
      } else {
        this.isChildDeleteDisabled.set(false);
      }
    }

    if (noIndex) {
      this.mainFormCheckboxChange(fieldName, event);
    } else {
      this.nestedFormCheckboxChange(fieldName, event, index);
    }

    this.parentCheckboxChange.emit(event?.checked);
    this.afterDeleteCheckboxChange?.();
  }

  public onPanelExpandedChange(isExpanded: boolean): void {
    if (this.isDisabled()) {
      return;
    }

    this.isExpanded.set(isExpanded);

    if (isExpanded) {
      return;
    }

    if (CtaDialogStore.expandedErrorDeliverablePanelId() === this.form().get('id')?.value) {
      CtaDialogStore.setExpandedErrorDeliverablePanel(null);
    }

    if (CtaDialogStore.expandedErrorExecutionPanelId() === this.form().get('id')?.value) {
      CtaDialogStore.setExpandedErrorExecutionPanel(null);
    }
  }

  public createPanelExpandedStateEffects(): void {
    effect(() => {
      if (!this.isExpanded()) {
        CtaDialogStore.setExpandedErrorDeliverablePanel(null);
      }

      if (this.expandOnAdd && this.form().get('isNew')?.value) {
        this.isExpanded.set(true);
        this.expandOnAdd = false;
      }
    });

    effect(() => {
      const expandedErrorExecutionPanelId = CtaDialogStore.expandedErrorExecutionPanelId();

      if (expandedErrorExecutionPanelId === this.form().get('id')?.value) {
        this.isExpanded.set(true);
        CtaDialogStore.setExpandedErrorExecutionPanel(null);
      }
    });

    effect(() => {
      const expandedErrorDeliverablePanelId = CtaDialogStore.expandedErrorDeliverablePanelId();

      if (expandedErrorDeliverablePanelId === this.form().get('id')?.value) {
        this.isExpanded.set(true);
        CtaDialogStore.setExpandedErrorDeliverablePanel(null);
      }
    });
  }

  protected afterDeleteCheckboxChange?(): void;
  protected beforeModeChange?(): void;
  protected afterModeChange?(): void;

  protected setupModeChangeEffect(): void {
    effect(() => {
      this.customValueCache.clear();

      this.beforeModeChange?.();
      this.setupMainFormModeChangeEffect();
      this.setupNestedFormModeChangeEffect();
      this.afterModeChange?.();
    });
  }

  protected onRevertToGlobalClick(fieldName: string, index?: number, event?: Event): void {
    event?.stopPropagation();
    this.customValueCache.clear();

    if (typeof index === 'number') {
      const form = this.customValueConfig.nestedArray?.getFormArray()?.controls?.[index] as FormGroup;

      this.customValuesService.revertToGlobalValue(
        form,
        fieldName,
        this.creatorId()!,
        this.customValueConfig.nestedArray?.managedFields?.find((field) => field.name === fieldName)?.type ??
          ECustomFieldType.string,
      );

      return;
    }

    this.customValuesService.revertToGlobalValue(
      this.customValueConfig.componentFields?.getForm(),
      fieldName,
      this.creatorId()!,
      this.customValueConfig.componentFields?.managedFields?.find((field) => field.name === fieldName)?.type ??
        ECustomFieldType.string,
    );
  }

  protected initializeValues(): void {
    this.formSubscriptionsForIsCustom = [];

    this.performMainFormInitialization();
    this.performNestedFormInitialization();

    if (this.isViewMode()) {
      this.disableForm();
    }
  }

  protected initializeNestedForm(form: FormGroup, managedFields: ICustomField[]): void {
    this.customValuesService.initializeGlobalValues(form, managedFields);

    this.formSubscriptionsForIsCustom.push(
      form.valueChanges.subscribe(() => {
        this.customValueCache.clear();
      }),
    );

    // Index within the nested array, used to disambiguate `source`/dirty-tracking for fields that
    // share one name across every row (e.g. every keyword's "text" control) - see
    // CustomValuesService.createFieldChangeTrackingSubscription().
    const itemIndex = this.customValueConfig.nestedArray?.getFormArray()?.controls.indexOf(form);
    const cleanup = this.customValuesService.startFormValueMonitoring(form, managedFields, {
      creatorId: this.creatorId()!,
      isCustomMode: () => this.isCustomMode(),
      itemIndex: itemIndex !== undefined && itemIndex >= 0 ? itemIndex : undefined,
      trackingContext: this.trackingContext,
    });

    this.nestedCleanups.set(form, cleanup);
  }

  protected disableForm(): void {
    this.isDisabled.set(true);
    this.form().disable({ emitEvent: false });
  }

  protected enableForm(): void {
    if (this.isViewMode()) {
      return;
    }

    this.isDisabled.set(false);
    this.form().enable({ emitEvent: false });
    this.customValueConfig.nestedArray?.getFormArray()?.controls?.forEach((control) => {
      if (
        (control.get('customCtaValues')?.value as ICustomCtaValue[])?.some(
          (value) =>
            value.creatorId === this.creatorId() &&
            value.status === EOverwriteStatus.deleted &&
            value.valueName === null,
        )
      ) {
        (control as FormGroup).disable({ emitEvent: false });
      }
    });
  }

  protected cleanup(): void {
    this.componentCleanup?.();
    this.nestedCleanups.forEach((cleanup) => cleanup());
    this.nestedCleanups.clear();
  }

  protected cleanupNestedForm(form: FormGroup): void {
    const cleanup = this.nestedCleanups.get(form);

    if (cleanup) {
      cleanup();
      this.nestedCleanups.delete(form);
    }
  }

  protected checkNestedFormDeleteDisableConditions(formArray: FormArray): void {
    if (this.isViewMode()) {
      return;
    }

    const filteredControls = formArray.controls.filter((control) =>
      this.getDeleteDisableCondition(control as FormGroup),
    );

    this.lastActiveChildElementDetails.set(
      CtaDetailsService.getLastActiveElementCreatorNames(formArray, this.isCustomMode(), this.creators()),
    );

    if (filteredControls.length === 1) {
      this.isChildDeleteDisabled.set(true);
    } else {
      this.isChildDeleteDisabled.set(false);
    }
  }

  private setupFormChangeEffect(): void {
    effect(() => {
      const newForm = this.form();

      if (newForm === this.currentForm) {
        return;
      }

      this.cleanup();
      this.currentForm = newForm;
      this.initializeValues();
    });
  }

  private setupCreatorIdChangeEffect(): void {
    effect(() => {
      const newCreatorId = this.creatorId();

      if (newCreatorId === this.currentCreatorId) {
        return;
      }

      this.customValueCache.clear();
      this.cleanup();
      this.startMonitoringWithCreator(newCreatorId);
      this.currentCreatorId = newCreatorId;
    });
  }

  private startMonitoringWithCreator(creatorId: string | TNil): void {
    if (this.customValueConfig.componentFields) {
      const { getForm, managedFields } = this.customValueConfig.componentFields;
      const form = getForm();
      this.componentCleanup = this.customValuesService.startFormValueMonitoring(form, managedFields, {
        creatorId,
        isCustomMode: this.isCustomMode,
        trackingContext: this.trackingContext,
      });
    }

    if (this.customValueConfig.nestedArray) {
      const { getFormArray, managedFields } = this.customValueConfig.nestedArray;
      const formArray = getFormArray();

      formArray.controls.forEach((control, itemIndex) => {
        const formGroup = control as FormGroup;
        const cleanup = this.customValuesService.startFormValueMonitoring(formGroup, managedFields, {
          creatorId,
          isCustomMode: this.isCustomMode,
          itemIndex,
          trackingContext: this.trackingContext,
        });
        this.nestedCleanups.set(formGroup, cleanup);
      });
    }
  }

  private mainFormCheckboxChange(fieldName: string, event: MatCheckboxChange, index?: number): void {
    // LF-4 (audit 2026-08-06, revised) - the whole-row per-creator checkbox ('id') toggles a
    // "deleted for this creator" marker instead of removing the row; report the transition as an
    // immediate added/removed event with the creatorId. Direction comes from event.checked (the
    // UI contract: checked = the row is active for this creator, so unchecking deletes it for
    // them and checking restores it) rather than reading the form back after the marker toggle -
    // customDelete()'s marker bookkeeping depends on the form's customCtaValues state, which
    // isn't a reliable read-back here.
    if (fieldName === 'id') {
      this.customValuesService.customDelete(this.customValueConfig, this.creatorId()!, fieldName, index);

      // Round-4 review (2026-08-06) - params named for what they are (the UI contract: checked =
      // the row is active for this creator): event.checked is the post-toggle active state, and
      // a toggle always flips it, so the pre-toggle state is its negation.
      this.trackPerCreatorRowToggle(this.creatorId()!, !event.checked, event.checked);
    } else {
      this.customValuesService.customDelete(this.customValueConfig, this.creatorId()!, fieldName, index);
    }

    if (!event?.checked) {
      this.disableForm();
    }
  }

  // LF-4 - fires the immediate added/removed pair for a per-creator deleted-marker toggle. The row
  // stays in the form (the backend resolves the marker), so the event's creatorId is the marker's;
  // the _saved counterparts fire from campaign-form.component.ts's baseline diff. Round-4 review
  // (2026-08-06) - also reports campaignCreationIsOverride (per-creator checkboxes only render in
  // custom mode, so it's always true here - kept for shape consistency with the other immediate
  // flow-dialog events).
  private trackPerCreatorRowToggle(creatorId: string, wasActive: boolean, isActiveNow: boolean): void {
    const { trackingContext } = this;
    const isRemoved = !isActiveNow;
    const properties = {
      campaignCreationCurrentStep: trackingContext.kind === 'execution' ? 3 : trackingContext.kind === 'kpi' ? 4 : 2,
      campaignCreationIsOverride: this.isCustomMode(),
      creatorId,
      ctaTemplateId: trackingContext.getCtaTemplateId(),
    };

    if (trackingContext.kind === 'execution') {
      this.eventTrackingService.track(
        isRemoved ? EEventType.campaignCreationExecutionRemoved : EEventType.campaignCreationExecutionAdded,
        { ...properties, executionId: trackingContext.getExecutionId() },
      );

      return;
    }

    if (trackingContext.kind === 'kpi') {
      // No KPI row renders the per-creator checkbox today, but keep the branch typed and correct
      // if one ever does - same shape as custom-values.service.ts's trackKpiFieldChange().
      this.eventTrackingService.track(
        isRemoved ? EEventType.campaignCreationKpiRemoved : EEventType.campaignCreationKpiAdded,
        {
          ...properties,
          executionId: trackingContext.getExecutionId(),
          expectedKpiId: trackingContext.getExpectedKpiId(),
          source: snakeCase(trackingContext.getKpi() ?? ''),
        },
      );

      return;
    }

    this.eventTrackingService.track(
      isRemoved ? EEventType.campaignCreationDeliverableRemoved : EEventType.campaignCreationDeliverableAdded,
      {
        ...properties,
        deliverableId: trackingContext.getDeliverableId(),
        deliverableType: trackingContext.deliverableType,
      },
    );
  }

  private nestedFormCheckboxChange(fieldName: string, event: MatCheckboxChange, index?: number): void {
    const availableChildren =
      this.customValueConfig.nestedArray
        ?.getFormArray()
        ?.controls?.filter((control) => this.getCustomModeDeleteDisableCondition(control as FormGroup)) ?? [];
    const isUnchecked = event && !event.checked;
    const isLastChildBeingUnchecked = availableChildren?.length === 1 && isUnchecked;
    const onlyOneChildRemains = availableChildren?.length - 1 === 1 && isUnchecked;

    if (isLastChildBeingUnchecked && !this.isChildOptional()) {
      event.source.checked = true;
      this.isChildDeleteDisabled.set(true);

      return;
    }

    this.customValuesService.customDelete(this.customValueConfig, this.creatorId()!, fieldName, index);

    // Round-3 audit - the nested-row per-creator checkbox ('id') toggles a "deleted for this
    // creator" marker; report it as a structural deliverable_changed, mirroring the trash path.
    // Source uses the row's content field (first non-'id' managed field), same convention as the
    // trash path. Round-4 review (2026-08-06) - the dirty-mark side is now net-aware via
    // trackNestedRowMarkerToggle (a marker applied then removed before saving must not leave the
    // row dirty, or changed_saved would fabricate).
    if (fieldName === 'id' && this.trackingContext.kind === 'deliverable' && typeof index === 'number') {
      this.trackNestedRowMarkerToggle(index, event.checked);
    }

    if (onlyOneChildRemains && !this.isChildOptional()) {
      this.isChildDeleteDisabled.set(true);
    }
  }

  // Round-4 review (2026-08-06) - the nested-row per-creator checkbox toggles a deleted-marker
  // (the row stays in the FormArray, disabled); the immediate deliverable_changed fires on every
  // toggle (same convention as the trash path), but the _saved dirty mark must net out when the
  // marker ends up back where it started this session (e.g. uncheck then re-check before saving) -
  // otherwise changed_saved fires for a row nothing changed on. A per-creator-per-source baseline
  // captured on the first toggle of the session is the comparison point (marker present !=
  // baseline => net change). The per-instance map resets naturally on the post-save form rebuild
  // (new directive instance = new session).
  //
  // Round-5 review (2026-08-06) - keyed by creatorId + source, not source alone: the directive
  // instance survives creator switches (setupCreatorIdChangeEffect re-initializes monitoring on
  // the same instance) and markers are per-creator, so a baseline captured for one creator's
  // toggle must never be reused for another creator's toggle on the same row (that could wrongly
  // mark the second creator's genuine change as a net no-op).
  private readonly nestedMarkerBaselineByKey = new Map<string, boolean>();

  private trackNestedRowMarkerToggle(index: number, isRowActiveNow: boolean): void {
    const { trackingContext } = this;

    if (trackingContext.kind !== 'deliverable') {
      return;
    }

    const contentFieldName =
      this.customValueConfig.nestedArray?.managedFields?.find((field) => field.name !== 'id')?.name ?? 'id';
    const source = `${snakeCase(contentFieldName)}_${index + 1}`;
    const baselineKey = `${this.creatorId() ?? 'no-creator'}_${source}`;

    // unchecked = the row is deleted for this creator = a marker is present.
    const markerPresentNow = !isRowActiveNow;

    if (!this.nestedMarkerBaselineByKey.has(baselineKey)) {
      // First toggle for this creator+source in the session: the pre-toggle state is the flip of
      // the post-toggle state (and, since only toggles mutate markers, equals the session-start
      // state - including for rows that loaded with a persisted marker).
      this.nestedMarkerBaselineByKey.set(baselineKey, !markerPresentNow);
    }

    const baselineMarkerPresent = this.nestedMarkerBaselineByKey.get(baselineKey)!;
    const isNetChange = markerPresentNow !== baselineMarkerPresent;

    this.customValuesService.trackDeliverableMarkerToggle(
      { creatorId: this.creatorId(), isCustomMode: this.isCustomMode, trackingContext },
      source,
      isNetChange,
    );
  }

  private getDeleteDisableCondition(control: FormGroup): boolean {
    return this.getCustomModeDeleteDisableCondition(control) || this.getGlobalModeDeleteDisableCondition(control);
  }

  private getGlobalModeDeleteDisableCondition(control: FormGroup): boolean {
    return !this.isCustomMode() && control.get('exclusiveTo')?.value === null;
  }

  private getCustomModeDeleteDisableCondition(control: FormGroup): boolean {
    return (
      this.isCustomMode() &&
      ((!(control.get('customCtaValues')?.value as ICustomCtaValue[])?.some(
        (customCtaValue) =>
          customCtaValue.creatorId === this.creatorId() &&
          customCtaValue.valueName === null &&
          customCtaValue.status === EOverwriteStatus.deleted,
      ) &&
        control.get('exclusiveTo')?.value === null) ||
        control.get('exclusiveTo')?.value === this.creatorId())
    );
  }

  private performMainFormInitialization(): void {
    if (!this.customValueConfig.componentFields) {
      return;
    }

    const form = this.customValueConfig.componentFields.getForm();

    this.formSubscriptionsForIsCustom.push(
      form.valueChanges.subscribe(() => {
        this.customValueCache.clear();
      }),
    );
    this.customValuesService.initializeGlobalValues(form, this.customValueConfig.componentFields.managedFields);

    this.componentCleanup = this.customValuesService.startFormValueMonitoring(
      form,
      this.customValueConfig.componentFields.managedFields,
      {
        creatorId: this.creatorId()!,
        isCustomMode: () => this.isCustomMode(),
        trackingContext: this.trackingContext,
      },
    );
  }

  private performNestedFormInitialization(): void {
    this.customValueConfig.nestedArray?.getFormArray()?.controls?.forEach((control) => {
      this.initializeNestedForm(control as FormGroup, this.customValueConfig.nestedArray!.managedFields);
    });
  }

  private setupMainFormModeChangeEffect(): void {
    if (this.customValueConfig.componentFields) {
      const form = this.customValueConfig.componentFields.getForm();

      if (this.isCustomMode()) {
        this.checkMainFormDisableConditions(form);
        this.customValuesService.switchToCustomMode(
          form,
          this.customValueConfig.componentFields.managedFields,
          this.creatorId()!,
          this.isViewMode(),
        );
      } else {
        this.enableForm();
        this.customValuesService.switchToGlobalMode(form, this.customValueConfig.componentFields.managedFields);
      }
    }
  }

  private setupNestedFormModeChangeEffect(): void {
    if (this.customValueConfig.nestedArray?.getFormArray()) {
      this.checkNestedFormDeleteDisableConditions(this.customValueConfig.nestedArray.getFormArray());
    }

    this.customValueConfig.nestedArray?.getFormArray().controls.forEach((control) => {
      const formGroup = control as FormGroup;

      if (this.isCustomMode()) {
        this.customValuesService.switchToCustomMode(
          formGroup,
          this.customValueConfig.nestedArray!.managedFields,
          this.creatorId()!,
          this.isViewMode(),
        );
      } else {
        this.enableForm();
        this.customValuesService.switchToGlobalMode(formGroup, this.customValueConfig.nestedArray!.managedFields);
      }
    });
  }

  private checkMainFormDisableConditions(form: FormGroup): void {
    // KPI rows never disable on a deleted marker - they render as an empty, editable value with
    // the revert button as the restore affordance (the 'kpi' control only exists on KPI forms).
    if (
      !form.get('kpi') &&
      (form.get('customCtaValues')?.value as ICustomCtaValue[])?.some(
        (customCtaValue) =>
          customCtaValue.creatorId === this.creatorId() &&
          customCtaValue.valueName === null &&
          customCtaValue.status === EOverwriteStatus.deleted,
      )
    ) {
      this.disableForm();
    }
  }
}
